import type { FC } from 'react';
import React, { Fragment, useEffect, useState } from 'react';
import { RelayEnvironmentProvider } from 'react-relay';
import { Environment } from 'relay-runtime';

// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';
import { css, styled, cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { getDefaultRelayEnvironmentConfig } from '@atlassian/relay-environment-config';

import type { IEditorPreloader } from '@confluence/edit-button/entry-points/types.ts';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { PageLayout } from '@confluence/page-layout/entry-points/PageLayout.ts';
import { useATLGeneralWebPanelObserver } from '@confluence/web-panel-location/entry-points/useATLGeneralWebPanelObserver.ts';
import { WebPanelLocation } from '@confluence/web-panel-location/entry-points/WebPanelLocation.ts';
import { useForgePageBannerObserver } from '@confluence/forge-ui/entry-points/useForgePageBannerObserver.ts';
import { ReturnToMobile } from '@confluence/return-to-mobile';
import { PERFORMANCE_SUBJECT_mainLayout } from '@confluence/performance/entry-points/constants.ts';
import { PerformanceEnd } from '@confluence/performance/entry-points/PerformanceEnd.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { fg } from '@confluence/feature-gating';
import { ConfluencePreviewPanelsProvider } from '@confluence/preview-panel-api/entry-points/ConfluencePreviewPanelsProvider.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';

// @ts-ignore
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles
import * as styles from './styles.module.css';
import { BreakoutSSRInlineScript } from './atl-general-breakout-ssr.tsx';
import { RenderPageBannerForgeApps, RenderBackgroundScriptApps } from './ForgeApps.tsx';
import type { MainLayoutQuery_space_Space_lookAndFeel_LookAndFeel_content_ContentLookAndFeel_screen_ScreenLookAndFeel as MainLayoutScreenType } from './__types__/MainLayoutQuery.ts';

export const relayEnvironment = new Environment(getDefaultRelayEnvironmentConfig({}));

const webPanelWrapperStyles = cssMap({
	showPanel: {
		display: 'block',
	},
	hidePanel: {
		display: 'none',
	},
});

const contentBodyWrapperStyles = css({
	marginTop: 0,
	boxSizing: 'border-box',
	clear: 'both',
	position: 'relative',
	width: '100%',
	display: 'flex',
	flex: '0 1 auto',
	minHeight: 'unset',
});

const fixedWebPanelSpacerStyles = css({
	height: 'var(--atl-general-webpanel-height)',
});

const fixedForgeBannerSpacerStyles = css({
	height: 'var(--forge-page-banner-height)',
});

const mainContentContainerStyles = cssMap({
	default: {
		width: `calc(100% - ${token('space.025')})`,
		transition: 'width 300ms cubic-bezier(0.2, 0, 0, 1) 0s',
		flexFlow: 'row wrap',
		'@media print': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			width: '100% !important',
		},
		marginLeft: token('space.025'),
	},
	enableRightSidebar: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		width: `calc(
    100vw - var(--leftPanelWidth, 0px) - var(--leftSidebarWidth, 0px) -
      var(--rightPanelWidth, 0px) - var(--rightSidebarWidth, 0px)
  - ${token('space.025')}) !important`,
	},
	nav4Enabled: {
		width: '100%',
		marginLeft: token('space.0'),
	},
	addMinWidth: {
		minWidth: 700,
	},
});

const fixedWebPanelWrapperStyles = cssMap({
	default: {
		display: 'none',
		zIndex: 12, // needs to be above 10 so it covers floating sticky table rows
		width: '100%',
		position: 'fixed',
		backgroundColor: token('elevation.surface'),
		left: 'var(--leftSidebarWidth)',
		maxWidth: `calc( 100vw - var(--leftPanelWidth, 0px) - var(--leftSidebarWidth, 0px) - var(--rightPanelWidth, 0px) - var(--rightSidebarWidth, 0px) )`,
	},
	showPanel: {
		display: 'block',
	},
	isFullWidth: {
		left: '0',
		maxWidth: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ContentBody = styled.div({
	backgroundColor: token('elevation.surface'),
	boxSizing: 'border-box',
	verticalAlign: 'top',
	minHeight: 'initial',
	padding: 0,
	width: '100%',
});

type EditorPreloaderType = (config: IEditorPreloader) => JSX.Element | null;

export type MainLayoutComponentProps = {
	children: React.ReactElement;
	enableNavigation?: boolean;
	disableMinWidth?: boolean;
	screen?: MainLayoutScreenType;
	navView?: string;
	editorPreloader?: () => Promise<() => EditorPreloaderType>;
	isViewPage?: boolean;
	isEdit?: boolean;
};

export const MainLayoutInnerComponent: FC<MainLayoutComponentProps> = ({
	enableNavigation = true,
	disableMinWidth,
	screen,
	isViewPage = false,
	isEdit,
	children,
}) => {
	const isLivePage = useIsCurrentPageLive();
	const isNav4Enabled = useIsNav4Enabled();
	const isPdfExport =
		typeof window !== 'undefined' && PDF_EXPORT_PAGE.match(window.location.pathname);
	const ommitComponentsNotNeededForPdfExport =
		isPdfExport && fg('confluence_fe_omit_unneeded_comps_for_pdf_export');

	const {
		shouldShowATLGeneralWebPanel,
		isATLGeneralWebPanelFixed,
		fixedATLGeneralWebPanelRef,
		isFullWidth,
		shouldHidePanels,
	} = useATLGeneralWebPanelObserver(isViewPage, isEdit);

	const {
		isForgePageBannerFixed,
		fixedForgePageBannerRef,
		isForgePageBannerFullWidth,
		shouldHideForgePageBanner,
	} = useForgePageBannerObserver(isViewPage, isNav4Enabled, isEdit);

	const rightSidebarContext = useRightSidebarContext();
	const [enableRightSidebarCSS, setEnableRightSidebarCSS] = useState(false);
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const topElementStyle = {};
	if (screen) {
		for (const key in screen) {
			if (screen.hasOwnProperty(key)) {
				if (key.startsWith('background')) {
					topElementStyle[key] = screen[key];
				}
			}
		}
	}

	useEffect(() => {
		if (rightSidebarContext.getCurrent()) {
			setEnableRightSidebarCSS(true);
		}
	}, [rightSidebarContext]);

	const AtlGeneralWebPanel = isATLGeneralWebPanelFixed ? (
		<Fragment>
			<div
				css={[
					fixedWebPanelWrapperStyles['default'],
					!shouldHidePanels && fixedWebPanelWrapperStyles['showPanel'],
					!isNav4Enabled && isFullWidth && fixedWebPanelWrapperStyles['isFullWidth'],
				]}
				ref={fixedATLGeneralWebPanelRef}
				data-test-id="fixed-atl-general-wrapper"
				data-connect-container="fixed-atl-general-wrapper"
			>
				<WebPanelLocation location={ATL_GENERAL} fetchPolicy="cache-first" />
			</div>
			<div css={fixedWebPanelSpacerStyles} />
			{renderServerPlaceholder && (
				<BreakoutSSRInlineScript noOpSSRInlineScript={renderServerPlaceholder} />
			)}
		</Fragment>
	) : (
		<div
			css={[
				webPanelWrapperStyles['showPanel'],
				shouldHidePanels && webPanelWrapperStyles['hidePanel'],
			]}
			data-test-id="atl-general-wrapper"
		>
			<WebPanelLocation location={ATL_GENERAL} fetchPolicy="cache-first" />
		</div>
	);

	const ForgePageBannerExtensionPoint = isForgePageBannerFixed ? (
		<>
			<div
				data-test-id="fixed-forge-page-banner-wrapper-compiled"
				css={[
					fixedWebPanelWrapperStyles['default'],
					!shouldHideForgePageBanner && fixedWebPanelWrapperStyles['showPanel'],
					!isNav4Enabled && isForgePageBannerFullWidth && fixedWebPanelWrapperStyles['isFullWidth'],
				]}
				ref={fixedForgePageBannerRef}
			>
				<RenderPageBannerForgeApps />
			</div>
			<div css={fixedForgeBannerSpacerStyles} />
		</>
	) : (
		<div
			css={[
				webPanelWrapperStyles['showPanel'],
				shouldHideForgePageBanner && webPanelWrapperStyles['hidePanel'],
			]}
		>
			<RenderPageBannerForgeApps />
		</div>
	);

	return children ? (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			<div
				css={[
					mainContentContainerStyles['default'],
					!enableRightSidebarCSS &&
						!(disableMinWidth || !enableNavigation) &&
						mainContentContainerStyles['addMinWidth'],
					enableRightSidebarCSS && mainContentContainerStyles['enableRightSidebar'],
					// This style is applied at the end behind the feature gate so that it always takes precedence
					isNav4Enabled && mainContentContainerStyles['nav4Enabled'],
				]}
				/*
				 * Previously, this used to be a `<Global styles={[…]} />` from Emotion, GlobalImagePreviewStyling
				 * But in the Compiled migration, there is no alternative and feels like the best path forward for now.
				 */
				// TODO: remove this when confluence-previews no longer used
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className={styles.dialogStyle}
				data-test-id="confluence-main-content"
				data-vc="confluence-main-content-compiled"
			>
				{/* Extension point for rendering Connect apps at the top of the main layout */}
				{shouldShowATLGeneralWebPanel &&
					!ommitComponentsNotNeededForPdfExport &&
					AtlGeneralWebPanel}
				{/* Extension point for rendering Forge apps at the top of the main layout. */}
				{ForgePageBannerExtensionPoint}
				{/*Banner to allow user to switch from desktop to mobile version*/}
				<ReturnToMobile />

				<>
					<Subscribe to={[BannerStateContainer]}>
						{() => (
							<div css={contentBodyWrapperStyles}>
								<ContentBody
									data-test-id="content-body"
									data-vc="content-body-compiled"
									id="content-body"
									style={topElementStyle}
								>
									{children}
								</ContentBody>
							</div>
						)}
					</Subscribe>
					{/* Hides the panel's UI in non-page content types but keeps the content (vendors often use background scripts for this footer extension point) */}
					{!isEdit &&
						!isLivePage &&
						!ommitComponentsNotNeededForPdfExport &&
						(expVal('confluence_frontend_atl_footer_hydration_fix', 'isEnabled', false)
							? !renderServerPlaceholder
							: true) && (
							<div
								css={[
									webPanelWrapperStyles['showPanel'],
									shouldHidePanels && webPanelWrapperStyles['hidePanel'],
								]}
								data-test-id="atl-footer-wrapper"
							>
								<WebPanelLocation location={ATL_FOOTER} />
							</div>
						)}
					{/* Many Connect apps use atl.general and atl.footer Web Panels to run scripts across all Confluence pages.
					Forge background scripts provide the same functionality for apps on the Forge platform */}
					<RenderBackgroundScriptApps />
				</>
			</div>
		</ErrorBoundary>
	) : null;
};

export const MainLayoutComponent: FC<MainLayoutComponentProps> = (props) => {
	const { editorPreloader, navView, enableNavigation = true } = props;

	const [preloader, setPreloader] = useState<EditorPreloaderType>();

	const setEditorPreloader = async () => {
		if (editorPreloader) {
			const newPreloader = await editorPreloader();
			setPreloader(newPreloader);
		}
	};

	useEffect(() => {
		void setEditorPreloader();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	let content = (
		<SuspenseWithDataFetcher dataFetcherName="default">
			<MainLayoutInnerComponent {...props} />
		</SuspenseWithDataFetcher>
	);

	content = (
		<>
			{enableNavigation ? (
				<PageLayout editorPreloader={preloader} view={navView}>
					{content}
				</PageLayout>
			) : (
				content
			)}
			<PerformanceEnd subject={PERFORMANCE_SUBJECT_mainLayout} />
		</>
	);

	if (expValEquals('confluence_preview_panels_exp', 'isEnabled', true)) {
		content = (
			<RelayEnvironmentProvider
				environment={fg('cfe_use_confluence_relay_env') ? getRelayEnvironment() : relayEnvironment}
			>
				<ConfluencePreviewPanelsProvider>{content}</ConfluencePreviewPanelsProvider>
			</RelayEnvironmentProvider>
		);
	}

	return content;
};
